import type Phaser from 'phaser';
import type { Vec2 } from '../beam/geometry';

export interface BeamRenderSegment {
  a: Vec2;
  b: Vec2;
  color: number;
}

const HALO_WIDTH = 14;
const HALO_ALPHA = 0.1;
const BLOOM_WIDTH = 6;
const BLOOM_ALPHA = 0.28;
const CORE_WIDTH = 1.5;
const CORE_ALPHA = 0.95;

const DASH_LENGTH = 14;
const DASH_GAP = 28;
const DASH_PERIOD = DASH_LENGTH + DASH_GAP;
const SHIMMER_PERIOD_MS = 2600;
const SHIMMER_WIDTH = 0.7;
const SHIMMER_ALPHA = 0.55;
const SHIMMER_COLOR = 0xffffff;

/**
 * Draw the static layers of a beam (wide halo, mid bloom, bright core) onto
 * the given Graphics object. The shimmer overlay is animated separately —
 * see drawShimmerLayer.
 */
export function drawBeamSegment(
  gfx: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: number,
): void {
  gfx.lineStyle(HALO_WIDTH, color, HALO_ALPHA);
  gfx.lineBetween(x1, y1, x2, y2);
  gfx.lineStyle(BLOOM_WIDTH, color, BLOOM_ALPHA);
  gfx.lineBetween(x1, y1, x2, y2);
  gfx.lineStyle(CORE_WIDTH, color, CORE_ALPHA);
  gfx.lineBetween(x1, y1, x2, y2);
}

/**
 * Redraw the animated dashed shimmer over each segment. The dashes flow along
 * the beam direction with period SHIMMER_PERIOD_MS to suggest emission.
 *
 * Call this every frame (e.g. from Scene.update) with the segment list cached
 * from the last beam recompute.
 */
export function drawShimmerLayer(
  gfx: Phaser.GameObjects.Graphics,
  segments: ReadonlyArray<BeamRenderSegment>,
  timeMs: number,
): void {
  gfx.clear();
  if (segments.length === 0) return;

  const t = ((timeMs % SHIMMER_PERIOD_MS) / SHIMMER_PERIOD_MS) * DASH_PERIOD;
  gfx.lineStyle(SHIMMER_WIDTH, SHIMMER_COLOR, SHIMMER_ALPHA);

  for (const seg of segments) {
    const dx = seg.b.x - seg.a.x;
    const dy = seg.b.y - seg.a.y;
    const len = Math.hypot(dx, dy);
    if (len < 0.001) continue;
    const ux = dx / len;
    const uy = dy / len;

    // Start a dash one full period before the segment start so the first
    // partial dash flows in cleanly.
    let cursor = -t;
    while (cursor < len) {
      const dashStart = Math.max(0, cursor);
      const dashEnd = Math.min(len, cursor + DASH_LENGTH);
      if (dashEnd > dashStart) {
        const sx = seg.a.x + ux * dashStart;
        const sy = seg.a.y + uy * dashStart;
        const ex = seg.a.x + ux * dashEnd;
        const ey = seg.a.y + uy * dashEnd;
        gfx.lineBetween(sx, sy, ex, ey);
      }
      cursor += DASH_PERIOD;
    }
  }
}
